import React, { useState, useRef } from 'react';
import { Popover, ScrollArea } from '@mantine/core';

const CustomMultiSelect = ({
  data = [],
  value = [],
  onChange,
  valueComponent: ValueComponent,
  placeholder = 'Auswahl',
  disabled = false,
  searchable = false,
  rightSection,
  style,
  className,
  onClick,
  label
}) => {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  // Safely resolve the selected items from the data array
  const selectedItems = value.map(val => data.find(item => item.value === val || item.id === val)).filter(Boolean);

  // Filter options based on the search input
  const filteredData = data.filter(item => {
    const itemLabel = item.label || item.name || '';
    return itemLabel.toLowerCase().includes(search.toLowerCase());
  });

  // Handle clicking an option in the dropdown
  const toggleItem = (itemValue) => {
    if (!onChange) return;
    if (value.includes(itemValue)) {
      onChange(value.filter(v => v !== itemValue));
    } else {
      onChange([...value, itemValue]);
    }
    setSearch('');
    if (inputRef.current) inputRef.current.focus();
  };

  // Handle clicking the main container
  const containerClick = (e) => {
    if (disabled) return;
    if (onClick) onClick(e);
    if (searchable) {
      setOpened(true);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  return (
    <div style={{ width: '100%', ...style }}>
      {/* Optional Top Label */}
      {label && <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px', color: '#212529' }}>{label}</div>}
      
      <Popover 
        opened={opened && !disabled} 
        onChange={setOpened} 
        width="target" 
        position="bottom-start" 
        withArrow={false}
        withinPortal
        zIndex={9999999}
      >
        <Popover.Target>
          <div
            className={`mantine-Input-wrapper ${className || ''}`}
            style={{
              minHeight: '36px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              backgroundColor: disabled ? '#f1f3f5' : '#fff',
              cursor: disabled ? 'not-allowed' : (searchable ? 'text' : 'pointer'),
              display: 'flex',
              alignItems: 'center',
              padding: '4px 10px',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
            onClick={containerClick}
          >
            <div
              style={{
                display: 'flex',
                flex: 1,
                gap: '5px',
                alignItems: 'center',
                overflowX: 'auto',
                minWidth: 0,
                paddingBottom: '2px',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
            >
              {selectedItems.map((item) => {
                const componentProps = {
                  ...item,
                  label: item.label || item.name,
                  value: item.value || item.id,
                };

                return (
                  <div
                    key={item.value || item.id}
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ flexShrink: 0 }}
                  >
                    {/* Render the Custom ValueComponent if provided */}
                    {ValueComponent ? (
                      <ValueComponent 
                        {...componentProps} 
                        onRemove={(e) => {
                          if (e) { e.stopPropagation(); e.preventDefault(); }
                          if (onChange) onChange(value.filter(v => v !== componentProps.value));
                        }}
                      />
                    ) : (
                      <span style={{ backgroundColor: '#f1f3f5', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #ced4da' }}>
                        {componentProps.label}
                        {onChange && (
                          <span 
                            style={{cursor: 'pointer', fontWeight: 'bold', marginLeft: '4px', color: '#868e96'}}
                            onClick={(e) => { e.stopPropagation(); onChange(value.filter(v => v !== componentProps.value)); }}
                          >×</span>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}

              {/* The Search Input */}
              {searchable && !disabled ? (
                <input
                  ref={inputRef}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setOpened(true); }}
                  placeholder={selectedItems.length === 0 ? placeholder : ''}
                  className='custom-multi-select-input'
                  style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, minWidth: '60px', fontSize: '14px', color: '#495057' }}
                />
              ) : (
                selectedItems.length === 0 && (
                  <span style={{ color: '#adb5bd', fontSize: '14px', flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {placeholder}
                  </span>
                )
              )}
            </div>

            {rightSection && (
              <div 
                style={{ paddingLeft: '8px', display: 'flex', alignItems: 'center', flexShrink: 0, cursor: 'pointer' }} 
                onClick={(e) => { if(searchable){ e.stopPropagation(); setOpened(!opened); } }}
              >
                {rightSection}
              </div>
            )}
          </div>
        </Popover.Target>

        {/* The Dropdown Menu */}
        <Popover.Dropdown p={0} style={{ border: '1px solid #ced4da', borderRadius: '4px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <ScrollArea.Autosize mah={250} type="always">
            {filteredData.length > 0 ? (
              filteredData.map((item) => {
                const isSelected = value.includes(item.value || item.id);
                return (
                  <div
                    key={item.value || item.id}
                    onClick={(e) => { e.stopPropagation(); toggleItem(item.value || item.id); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', backgroundColor: isSelected ? '#e6e6ff' : 'transparent', fontSize: '12px', color: '#495057', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f3f5' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#e6e6ff' : '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#e6e6ff' : 'transparent'}
                  >
                    <span style={{fontWeight: '400'}}>{item.label || item.name}</span>
                    {/* {isSelected && <span style={{ color: '#5555FF', fontWeight: 'bold' }}>✓</span>} */}
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '8px 12px', color: '#868e96', fontSize: '14px', textAlign: 'center' }}>
                Nichts gefunden
              </div>
            )}
          </ScrollArea.Autosize>
        </Popover.Dropdown>
      </Popover>
    </div>
  );
};

export default CustomMultiSelect;